import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

import { API_URL } from "@/utils/api";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [actionTasksCount, setActionTasksCount] = useState(0);

  // Load notifications from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("finai_notifications");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      } catch (e) {
        console.error("Failed to parse notifications:", e);
      }
    }
  }, []);

  // Fetch pending action tasks count
  const fetchActionTasksCount = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/user/action-tasks/count`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActionTasksCount(data.pending_count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch action tasks count:", err);
    }
  }, []);

  // Fetch action tasks count on mount and periodically
  useEffect(() => {
    fetchActionTasksCount();
    const interval = setInterval(fetchActionTasksCount, 60000); // Every minute
    return () => clearInterval(interval);
  }, [fetchActionTasksCount]);

  // Save notifications to localStorage
  const saveNotifications = useCallback((notifs) => {
    localStorage.setItem("finai_notifications", JSON.stringify(notifs.slice(0, 50))); // Keep last 50
  }, []);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    const newNotif = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };

    setNotifications(prev => {
      const updated = [newNotif, ...prev].slice(0, 50);
      saveNotifications(updated);
      return updated;
    });
    setUnreadCount(prev => prev + 1);

    // Show toast for immediate feedback
    if (notification.showToast !== false) {
      const toastType = notification.type === "error" ? toast.error : 
                       notification.type === "success" ? toast.success : 
                       notification.type === "warning" ? toast.warning : toast;
      toastType(notification.title, {
        description: notification.message
      });
    }

    return newNotif;
  }, [saveNotifications]);

  // Mark notification as read
  const markAsRead = useCallback((notifId) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notifId ? { ...n, read: true } : n
      );
      saveNotifications(updated);
      return updated;
    });
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, [saveNotifications]);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      saveNotifications(updated);
      return updated;
    });
    setUnreadCount(0);
  }, [saveNotifications]);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem("finai_notifications");
  }, []);

  // Remove a specific notification
  const removeNotification = useCallback((notifId) => {
    setNotifications(prev => {
      const notif = prev.find(n => n.id === notifId);
      const updated = prev.filter(n => n.id !== notifId);
      saveNotifications(updated);
      if (notif && !notif.read) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      return updated;
    });
  }, [saveNotifications]);

  // Notification types helpers
  const notify = {
    success: (title, message, options = {}) => addNotification({ type: "success", title, message, ...options }),
    error: (title, message, options = {}) => addNotification({ type: "error", title, message, ...options }),
    warning: (title, message, options = {}) => addNotification({ type: "warning", title, message, ...options }),
    info: (title, message, options = {}) => addNotification({ type: "info", title, message, ...options }),
    
    // Specific notification types
    invoiceCreated: (invoiceId, amount) => addNotification({
      type: "success",
      title: "Invoice Created",
      message: `Invoice #${invoiceId} for $${amount.toFixed(2)} has been created`,
      category: "invoice",
      actionUrl: "/dashboard/invoicing"
    }),
    invoiceSent: (invoiceId, clientEmail) => addNotification({
      type: "success", 
      title: "Invoice Sent",
      message: `Invoice #${invoiceId} has been sent to ${clientEmail}`,
      category: "invoice",
      actionUrl: "/dashboard/invoicing"
    }),
    invoicePaid: (invoiceId, amount) => addNotification({
      type: "success",
      title: "Payment Received!",
      message: `Invoice #${invoiceId} - $${amount.toFixed(2)} has been paid`,
      category: "invoice",
      actionUrl: "/dashboard/invoicing"
    }),
    contractSigned: (contractTitle) => addNotification({
      type: "success",
      title: "Contract Signed",
      message: `"${contractTitle}" has been signed successfully`,
      category: "contract",
      actionUrl: "/dashboard/contracts"
    }),
    documentUploaded: (filename) => addNotification({
      type: "info",
      title: "Document Uploaded",
      message: `"${filename}" has been uploaded successfully`,
      category: "document",
      actionUrl: "/dashboard/documents"
    }),
    documentAnalyzed: (filename) => addNotification({
      type: "success",
      title: "Analysis Complete",
      message: `AI analysis of "${filename}" is ready to view`,
      category: "document",
      actionUrl: "/dashboard/documents"
    }),
    taskDue: (taskTitle, dueDate) => addNotification({
      type: "warning",
      title: "Task Due Soon",
      message: `"${taskTitle}" is due on ${new Date(dueDate).toLocaleDateString()}`,
      category: "task",
      actionUrl: "/dashboard/tasks"
    }),
    taxFormGenerated: (formType) => addNotification({
      type: "success",
      title: "Tax Form Generated",
      message: `${formType} form has been generated and is ready for download`,
      category: "tax",
      actionUrl: "/dashboard/tax-hub"
    })
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      actionTasksCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearAll,
      removeNotification,
      refreshActionTasks: fetchActionTasksCount,
      notify
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}

export default NotificationContext;
